import { compact } from 'lodash';

import { ClientProfileRelationshipType } from '@/types/schema';

import { GRANTOR_RELATIONSHIP_COPY_MAP } from './relationshipCopy.constants';

/**
 * A partial selection of fields available on ClientProfileRelationship
 */
interface RelationshipData {
  type: ClientProfileRelationshipType;
  toClientProfile: {
    id: string;
  };
}

/**
 * A partial selection of fields available on ClientProfile
 */
interface ClientProfile {
  id: string;
  displayGivenName: string;
  isPrimary: boolean;
}

function isClientProfileRelationshipType(
  key: string
): key is ClientProfileRelationshipType {
  return key in GRANTOR_RELATIONSHIP_COPY_MAP;
}

export function getRelationshipCopy(
  relationships: RelationshipData[],
  primaryClients: ClientProfile[]
): string {
  const relationshipsWithPrimaryClients = relationships.filter((rel) => {
    const relatedClient = primaryClients.find(
      (client) => client.id === rel.toClientProfile.id && client.isPrimary
    );
    return !!relatedClient;
  });

  if (relationshipsWithPrimaryClients.length === 0) {
    return '';
  }

  const relationshipsByType: Partial<
    Record<ClientProfileRelationshipType, string[]>
  > = {};

  relationshipsWithPrimaryClients.forEach((rel) => {
    const relatedClient = primaryClients.find(
      (client) => client.id === rel.toClientProfile.id
    );

    if (relatedClient?.displayGivenName) {
      const type = rel.type;
      if (!relationshipsByType[type]) {
        relationshipsByType[type] = [];
      }

      relationshipsByType[type]!.push(relatedClient.displayGivenName);
    }
  });

  const relationshipsText = Object.entries(relationshipsByType)
    // filter types to avoid casting
    .filter((entry): entry is [ClientProfileRelationshipType, string[]] =>
      isClientProfileRelationshipType(entry[0])
    )
    .map(([type, names]) => {
      const formattedType = GRANTOR_RELATIONSHIP_COPY_MAP[type];
      if (!formattedType) {
        return null;
      }
      return names.length > 1
        ? `${formattedType} of ${names.join(' & ')}`
        : `${formattedType} of ${names[0]}`;
    })
    .filter(Boolean);

  return compact(relationshipsText).join(', ');
}
